/**
 * Notification Slice
 *
 * Global notification management. Pages dispatch addNotification()
 * and the App layout renders them via Cloudscape Flashbar.
 *
 * Usage:
 *   dispatch(addNotification({ type: 'error', header: 'Failed to load', content: 'Network error' }));
 *   dispatch(dismissNotification(id));
 *   dispatch(clearNotifications());
 */
import { createSlice, PayloadAction, nanoid } from '@reduxjs/toolkit';
import { notificationInitialState, AppNotification, NotificationType } from './notificationState';

const notificationSlice = createSlice({
  name: 'notifications',
  initialState: notificationInitialState,
  reducers: {
    addNotification: {
      // Upsert by id: passing a stable id replaces that notification in place
      // (so re-raising the same error does not stack); omit it for a one-off.
      reducer: (state, action: PayloadAction<AppNotification>) => {
        const idx = state.items.findIndex((n) => n.id === action.payload.id);
        if (idx >= 0) state.items[idx] = action.payload;
        else state.items.push(action.payload);
      },
      prepare: (payload: {
        id?: string; type: NotificationType; header: string; content?: string; dismissible?: boolean;
      }) => ({
        payload: { dismissible: true, ...payload, id: payload.id ?? nanoid() },
      }),
    },
    dismissNotification: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(n => n.id !== action.payload);
    },
    clearNotifications: (state) => {
      state.items = [];
    },
  },
});

export const { addNotification, dismissNotification, clearNotifications } = notificationSlice.actions;
export default notificationSlice.reducer;
